import React, { ReactElement, Suspense, useMemo } from "react";
import { useTranslationsReady, useWebTranslation } from "../../translations";
import { Layout } from "../../components/PageTemplates/Layout";
import { ApolloProvider } from "@apollo/client";
import client from "../../../apollo-client";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { Provider } from "react-redux";
import { createStore } from "components/Signup/store";
import { SignUpFlow } from "components/Signup/flow/SignupFlow/SignUpFlow";
import { stateStore } from "components/Signup/flow/utils/stateStore";
import { applyStateFromQuery } from "components/Signup/flow/utils/applyStateFromQuery";
import { getInitialState } from "components/Signup/store/state";
import { fetchQuotePreview } from "components/Signup/flow/utils/fetchQuotePreviews";
import { State } from "components/Signup/store/state/types";
import { resumeDraft } from "components/Signup/flow/utils/resumeDraft/resumeDraft";
import useIntercom from "hooks/useIntercom";
import { StepName } from "components/Signup/steps/types";
import { parseUrl } from "components/Signup/flow/utils/parseUrlQuery";

const getDiscountCode = (context: GetServerSidePropsContext) => {
  if (Array.isArray(context.query.discountCode)) {
    return context.query.discountCode[0];
  }
  return context.query.discountCode ?? null;
};

const loadedState = stateStore.load();

export default function StepPage({
  hydratedState,
}: {
  hydratedState: State;
}): ReactElement {
  const ready = useTranslationsReady();
  const { t } = useWebTranslation();

  const store = useMemo(() => {
    return createStore({
      ...(hydratedState.metadata.isResumed
        ? hydratedState
        : { ...hydratedState, ...loadedState }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't rehydrate the store
  }, []);

  useIntercom({ hideDefaultLauncher: true });

  if (!ready) {
    return <></>;
  }

  return (
    <ApolloProvider client={client}>
      <Provider store={store}>
        <Layout
          title={t("signup.meta.title")}
          description={t("signup.meta.description")}
          noRobot
        >
          <Suspense>
            <SignUpFlow />
          </Suspense>
        </Layout>
      </Provider>
    </ApolloProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  if (!context.query?.id) {
    return {
      props: {
        hydratedState: getInitialState(),
      },
    };
  }

  try {
    // The old resume functionality used uuid's. We use much cooler and shorter IDs now.
    if (context.query.id.length < 20) {
      const hydratedState = await resumeDraft({
        id: context.query.id as string,
        step: context.query.step?.[0] as StepName,
        discountCode: getDiscountCode(context),
      });

      return {
        props: {
          hydratedState,
        },
      };
    } else {
      // Legacy support
      const hydratedState = await fetchQuotePreview(context.query.id as string);

      if (hydratedState) {
        return {
          props: {
            hydratedState: applyStateFromQuery(
              hydratedState,
              getInitialState(),
              []
            ),
          },
        };
      }

      return {
        props: {
          hydratedState: getInitialState(),
        },
      };
    }
  } catch (e) {
    console.error(
      `Error fetching quote preview with id ${context.query.id}`,
      e
    );
    const { stepName } = parseUrl(context.query);

    if (stepName === "quote-preview") {
      return {
        redirect: {
          destination: `${context.locale === "en" ? "/en" : ""}/signup/summary`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        hydratedState: getInitialState(),
      },
    };
  }
};
